import * as THREE from "three";
import { usePhysicsStore } from "../../../stores/physicsStore";
import { getSceneState } from "../../../stores/sceneStore";
import { convertPoseToLocal, applyPoseToObject } from "../../poseUtils";
import { devLog } from "../../scene/sceneConstants";
import { sanitizeMjcfName } from "../../../utils/mjcfNames";
import { buildBodyRootMap, findBodyForJointUuid } from "../../../utils/bodyHierarchy";
import { logger } from "../../../utils/logger";

export function createScenePoseApplier({
  externalForceVizRef,
  sceneRef,
  modelGroupRef,
  sceneObjectsRef,
  zeroBodySyncWarningSignatureRef,
  rolloutProbeRef,
  physicsPoseLogDoneRef,
  padPoseLogDoneRef,
  disposeAndRemove,
  mujocoVecToViewer,
}) {
  const toLocalTransform = (obj, worldPosArr, worldQuatArr) => {
    if (!obj) return;
    const parent = obj.parent;
    const modelGroup = modelGroupRef.current;
    const localPose = convertPoseToLocal(
      [
        worldPosArr[0],
        worldPosArr[1],
        worldPosArr[2],
        worldQuatArr[0],
        worldQuatArr[1],
        worldQuatArr[2],
        worldQuatArr[3],
      ],
      modelGroup,
      parent
    );
    applyPoseToObject(obj, localPose);
    // Debug: warn if any ancestor has non-uniform scale, which can cause visual skew when rotating
    try {
      const debugPoses = typeof window !== "undefined" && window.__debugPhysicsPoses === true;
      if (debugPoses) {
        const warnOnceKey = `scaleWarn:${obj.name || obj.uuid}`;
        if (!window.__scaleWarned) window.__scaleWarned = new Set();
        if (!window.__scaleWarned.has(warnOnceKey)) {
          let p = obj;
          let skew = false;
          while (p) {
            const s = new THREE.Vector3();
            if (p.getWorldScale) p.getWorldScale(s);
            if (Math.abs(s.x - s.y) > 1e-4 || Math.abs(s.y - s.z) > 1e-4) {
              skew = true;
              break;
            }
            p = p.parent;
          }
          if (skew) {
            window.__scaleWarned.add(warnOnceKey);
            logger.warn(
              "[Physics] Non-uniform world scale detected in parent chain for",
              obj.name || obj.uuid
            );
          }
        }
      }
    } catch (error) {
      logger.warn("[Scene] Unexpected error during scene update", error);
    }
  };

  let scenePoseMetadataCache = {
    bodies: null,
    worldBodies: null,
    joints: null,
    metadata: null,
  };

  const getScenePoseMetadata = () => {
    const state = getSceneState();
    const bodies = state.bodies || {};
    const worldBodies = state.worldBodies || [];
    const jointsByUuid = state.joints || {};

    if (
      scenePoseMetadataCache.metadata &&
      scenePoseMetadataCache.bodies === bodies &&
      scenePoseMetadataCache.worldBodies === worldBodies &&
      scenePoseMetadataCache.joints === jointsByUuid
    ) {
      return scenePoseMetadataCache.metadata;
    }

    const rootBodyUuids = new Set();
    const floatingRootAliases = new Set();
    const fixedRootAliases = new Set();
    const poseDrivenNames = new Set();
    const bodyAliasToSceneKey = new Map();

    const addBodyAliases = (set, body) => {
      if (!body?.uuid) return;
      set.add(body.uuid);
      set.add(sanitizeMjcfName(body.uuid));
      if (body.name) {
        set.add(body.name);
        set.add(sanitizeMjcfName(body.name));
      }
    };

    Object.values(bodies).forEach((body) => {
      if (!body) return;
      const sceneKey = body.uuid || sanitizeMjcfName(body.name || "");
      const aliases = [
        body.uuid,
        body.uuid ? sanitizeMjcfName(body.uuid) : null,
        body.name,
        body.name ? sanitizeMjcfName(body.name) : null,
      ].filter(Boolean);

      aliases.forEach((alias) => {
        if (!bodyAliasToSceneKey.has(alias)) {
          bodyAliasToSceneKey.set(alias, sceneKey);
        }
      });

      const hasJoints = Array.isArray(body.joints) && body.joints.length > 0;
      if (hasJoints || body.isMocap) {
        aliases.forEach((alias) => poseDrivenNames.add(alias));
      }
    });

    worldBodies.forEach((wb) => {
      const uuid = typeof wb === "string" ? wb : wb.uuid;
      if (uuid) {
        rootBodyUuids.add(uuid);
        rootBodyUuids.add(sanitizeMjcfName(uuid));
        const bodyEntry = bodies?.[uuid];
        if (bodyEntry?.name) {
          rootBodyUuids.add(bodyEntry.name);
          rootBodyUuids.add(sanitizeMjcfName(bodyEntry.name));
        }
      }
    });

    const bodyRootMap = buildBodyRootMap(bodies, worldBodies);
    const floatingRootUuids = new Set();
    const movingRootUuids = new Set();
    Object.values(jointsByUuid).forEach((joint) => {
      if (!joint || !joint.uuid) return;
      const hostBodyUuid = findBodyForJointUuid(bodies, joint.uuid);
      const rootUuid = hostBodyUuid ? bodyRootMap[hostBodyUuid] || hostBodyUuid : null;
      if (!rootUuid) return;
      if (joint.kind === "free") {
        floatingRootUuids.add(rootUuid);
      }
      if (hostBodyUuid === rootUuid) {
        movingRootUuids.add(rootUuid);
      }
    });

    worldBodies.forEach((wb) => {
      const uuid = typeof wb === "string" ? wb : wb.uuid;
      if (!uuid) return;
      const rootBody = bodies?.[uuid];
      if (floatingRootUuids.has(uuid)) {
        addBodyAliases(floatingRootAliases, rootBody);
      } else if (!movingRootUuids.has(uuid)) {
        addBodyAliases(fixedRootAliases, rootBody);
      }
    });

    const metadata = {
      rootBodyUuids,
      floatingRootAliases,
      fixedRootAliases,
      poseDrivenNames,
      bodyAliasToSceneKey,
    };
    scenePoseMetadataCache = {
      bodies,
      worldBodies,
      joints: jointsByUuid,
      metadata,
    };
    return metadata;
  };

  const applyLatestPoses = (lp) => {
    if (!usePhysicsStore.getState().isRunning) return;
    if (!lp) {
      if (typeof window !== "undefined" && window.__debugPhysicsPoses === true) {
        devLog("[Scene] applyLatestPoses called with null payload");
      }
      return;
    }
    const debugPoses = typeof window !== "undefined" && window.__debugPhysicsPoses === true;
    const debugVerbose =
      typeof window !== "undefined" && window.__debugPhysicsPosesVerbose === true;
    if (debugPoses) {
      devLog("[Scene] applyLatestPoses received", {
        bodyNamesCount: lp.bodyNames?.length,
        posesCount: lp.poses?.length,
        version: lp.version,
      });
    }
    const actions = usePhysicsStore.getState().actions;

    if (!lp.bodyNames || !lp.poses) {
      actions.setMappingStats(0, 0);
      return;
    }
    let names = lp.bodyNames.slice();
    const rawPoses = lp.poses;
    const poseStride = Number(lp.poseStride) || 7;
    const posesArePacked = ArrayBuffer.isView(rawPoses);
    let poseBaseIndex = 0;
    const getPoseCount = () =>
      posesArePacked
        ? Math.max(0, Math.floor(rawPoses.length / poseStride) - poseBaseIndex)
        : Math.max(0, rawPoses.length - poseBaseIndex);
    const getPoseAt = (index) => {
      const sourceIndex = index + poseBaseIndex;
      if (posesArePacked) {
        const start = sourceIndex * poseStride;
        if (start < 0 || start + 6 >= rawPoses.length) return null;
        return rawPoses.subarray(start, start + poseStride);
      }
      return rawPoses[sourceIndex] || null;
    };
    const poseCount = getPoseCount();
    if (!physicsPoseLogDoneRef.current && debugPoses) {
      try {
        const previewNames = names.slice(0, 8);
        const previewPoses = Array.from({ length: Math.min(8, poseCount) }, (_, idx) => {
          const pose = getPoseAt(idx);
          return pose ? Array.from(pose) : null;
        });
        devLog("[Physics->Viewer] pose preview names:", previewNames);
        devLog("[Physics->Viewer] pose preview values:", previewPoses);
        const modelGroup = modelGroupRef.current;
        if (modelGroup) {
          const groupQuat = new THREE.Quaternion();
          modelGroup.getWorldQuaternion(groupQuat);
          devLog("[Physics->Viewer] modelGroup world quat:", groupQuat.toArray());
        }
        const previewMapping = previewNames.map((rawName, idx) => {
          const obj = sceneObjectsRef.current[rawName] || null;
          if (!obj) {
            return { name: rawName, mapped: false };
          }
          const beforePos = new THREE.Vector3();
          const beforeQuat = new THREE.Quaternion();
          obj.getWorldPosition(beforePos);
          if (obj.getWorldQuaternion) obj.getWorldQuaternion(beforeQuat);

          // Investigate potential double-transform: if there is a descendant with the same name
          // then we might be applying poses to a container instead of the body's poseGroup
          let innerInfo = null;
          try {
            const inner =
              obj !== null && typeof obj.getObjectByName === "function"
                ? obj.getObjectByName(rawName)
                : null;
            if (inner && inner !== obj) {
              const innerPos = new THREE.Vector3();
              const innerQuat = new THREE.Quaternion();
              inner.getWorldPosition(innerPos);
              if (inner.getWorldQuaternion) inner.getWorldQuaternion(innerQuat);
              innerInfo = {
                foundSameNamedDescendant: true,
                innerWorldPos: innerPos.toArray(),
                innerWorldQuat: innerQuat.toArray(),
              };
            }
          } catch (error) {
            logger.warn("[Scene] Unexpected error during scene update", error);
          }

          return {
            name: rawName,
            mapped: true,
            beforePos: beforePos.toArray(),
            beforeQuat: beforeQuat.toArray(),
            pose: previewPoses[idx],
            inner: innerInfo || { foundSameNamedDescendant: false },
          };
        });
        devLog("[Physics->Viewer] mapping snapshot:", previewMapping);
        try {
          usePhysicsStore.getState().actions.addDiagnostic({
            source: "viewer",
            stage: "pose-preview",
            level: "info",
            message: "Received pose preview from MuJoCo",
            data: { names: previewNames, poseCount: previewPoses.length },
          });
        } catch (error) {
          logger.warn("[Scene] Unexpected error during scene update", error);
        }
      } catch (e) {
        logger.warn("[Physics->Viewer] pose preview logging failed:", e);
      }
      physicsPoseLogDoneRef.current = true;
    }

    const keys = Object.keys(sceneObjectsRef.current);
    const firstPose = getPoseAt(0);
    if (
      poseCount === keys.length + 1 &&
      (names[0] === "world" ||
        names[0] === "body_0" ||
        (firstPose?.[0] === 0 && firstPose?.[1] === 0 && firstPose?.[2] === 0))
    ) {
      names = names.slice(1);
      poseBaseIndex += 1;
    }

    // Cached scene-derived pose metadata. Mixed scenes are common: a fixed-base robot
    // plus free props should not make the fixed robot root pose-driven.
    let rootBodyUuids;
    let floatingRootAliases;
    let fixedRootAliases;
    let poseDrivenNames;
    let bodyAliasToSceneKey;
    try {
      ({
        rootBodyUuids,
        floatingRootAliases,
        fixedRootAliases,
        poseDrivenNames,
        bodyAliasToSceneKey,
      } = getScenePoseMetadata());
    } catch (error) {
      logger.warn("[Scene] Unexpected error during scene update", error);
      rootBodyUuids = new Set();
      floatingRootAliases = new Set();
      fixedRootAliases = new Set();
      poseDrivenNames = new Set();
      bodyAliasToSceneKey = new Map();
    }

    // (Removed noisy fixed-base debug logging; behavior unchanged.)

    if (debugPoses && !padPoseLogDoneRef.current) {
      try {
        const state = getSceneState();
        const bodies = state.bodies || {};
        const padBodies = Object.values(bodies).filter(
          (body) => body && (body.name === "left_pad" || body.name === "right_pad")
        );

        const modelGroup = modelGroupRef.current;
        const padSnapshots = padBodies.map((body) => {
          const uuid = body.uuid;
          const sanitizedUuid = uuid ? sanitizeMjcfName(uuid) : null;
          const idx = uuid ? names.indexOf(uuid) : -1;
          const sanitizedIdx = sanitizedUuid ? names.indexOf(sanitizedUuid) : -1;
          const poseIdx = idx !== -1 ? idx : sanitizedIdx;
          const pose = poseIdx !== -1 ? getPoseAt(poseIdx) : null;
          const obj =
            (uuid && sceneObjectsRef.current[uuid]) ||
            (sanitizedUuid && sceneObjectsRef.current[sanitizedUuid]) ||
            null;
          let worldPos = null;
          let worldQuat = null;
          let currentLocal = null;
          let computedLocal = null;
          if (obj) {
            const wp = new THREE.Vector3();
            const wq = new THREE.Quaternion();
            obj.getWorldPosition(wp);
            if (obj.getWorldQuaternion) obj.getWorldQuaternion(wq);
            worldPos = wp.toArray();
            worldQuat = wq.toArray();

            currentLocal = {
              position: obj.position.toArray(),
              quaternion: obj.quaternion.toArray(),
            };

            if (pose && Array.isArray(pose) && pose.length === 7) {
              const localPose = convertPoseToLocal(
                [pose[0], pose[1], pose[2], pose[3], pose[4], pose[5], pose[6]],
                modelGroup,
                obj.parent
              );
              if (localPose) {
                computedLocal = {
                  position: localPose.position.toArray(),
                  quaternion: localPose.quaternion.toArray(),
                };
              }
            }
          }

          return {
            name: body.name,
            uuid,
            sanitizedUuid,
            bodyPos: body.position,
            bodyQuat: body.orientation,
            poseIdx,
            pose,
            worldPos,
            worldQuat,
            currentLocal,
            computedLocal,
          };
        });

        devLog("[Physics->Viewer] pad pose snapshot", padSnapshots);
      } catch (error) {
        logger.warn("[Scene] pad pose snapshot failed", error);
      }
      padPoseLogDoneRef.current = true;
    }

    let mappedCount;

    const applyMappedPoses = ({ strictPoseDriven }) => {
      let localMapped = 0;
      for (let i = 0; i < names.length; i++) {
        const name = names[i];
        // Physics uses sanitized body UUIDs which map to scene objects.
        // Fallback: look up by matching body uuid/sanitized so IK-updated scene and physics stay in sync.
        let obj = sceneObjectsRef.current[name];
        if (!obj && typeof name === "string") {
          const mappedSceneKey = bodyAliasToSceneKey?.get(name);
          if (mappedSceneKey) {
            obj =
              sceneObjectsRef.current[mappedSceneKey] ||
              sceneObjectsRef.current[sanitizeMjcfName(mappedSceneKey)];
          }
        }
        const pose = getPoseAt(i);
        if (!obj || !pose) continue;

        // Only apply physics poses to bodies that own joints (or mocap bodies) when strict mode is enabled.
        // Fixed child bodies should inherit transforms from their parents to avoid double transforms.
        if (strictPoseDriven && poseDrivenNames && !poseDrivenNames.has(name)) {
          // Floating-base robots (e.g. freejoint root) still require root pose updates
          // even when the body has no explicit hinge joints in store metadata.
          const allowFloatingRoot = floatingRootAliases.has(name);
          if (!allowFloatingRoot) {
            continue;
          }
        }

        // Guard: for fixed-base robots, never apply the physics pose to the root body.
        // Rationale: we keep a Y-up/Z-up shim on modelGroup; applying base world pose can visually rotate the whole model.
        if (fixedRootAliases.has(name)) {
          continue;
        }

        if (debugPoses && debugVerbose) {
          // Before applying, capture world transforms of both the mapped object and any same-named descendant
          const worldPosBefore = new THREE.Vector3();
          const worldQuatBefore = new THREE.Quaternion();
          obj.getWorldPosition(worldPosBefore);
          if (obj.getWorldQuaternion) obj.getWorldQuaternion(worldQuatBefore);

          let innerBefore = null;
          try {
            const inner =
              obj !== null && typeof obj.getObjectByName === "function"
                ? obj.getObjectByName(name)
                : null;
            if (inner && inner !== obj) {
              const ip = new THREE.Vector3();
              const iq = new THREE.Quaternion();
              inner.getWorldPosition(ip);
              if (inner.getWorldQuaternion) inner.getWorldQuaternion(iq);
              innerBefore = { pos: ip.toArray(), quat: iq.toArray() };
            }
          } catch (error) {
            logger.warn("[Scene] Unexpected error during scene update", error);
          }

          devLog("[Scene] pose entry (pre-apply)", {
            name,
            pose,
            worldPos: worldPosBefore.toArray(),
            worldQuat: worldQuatBefore.toArray(),
            innerBefore,
          });
        }

        toLocalTransform(obj, [pose[0], pose[1], pose[2]], [pose[3], pose[4], pose[5], pose[6]]);
        // Keep joint limit visualizations in the authored parent frame during physics.
        // They intentionally live outside `poseGroup` so actuator-driven joint motion does not
        // drag the min/max arc; live overlays that need the simulated pivot sample `bodyGroup`
        // through setJointPivotWorldFromViz instead.
        localMapped += 1;

        if (debugPoses && debugVerbose) {
          // After applying, capture world transforms again
          const worldPosAfter = new THREE.Vector3();
          const worldQuatAfter = new THREE.Quaternion();
          obj.getWorldPosition(worldPosAfter);
          if (obj.getWorldQuaternion) obj.getWorldQuaternion(worldQuatAfter);

          let innerAfter = null;
          try {
            const inner =
              obj !== null && typeof obj.getObjectByName === "function"
                ? obj.getObjectByName(name)
                : null;
            if (inner && inner !== obj) {
              const ip = new THREE.Vector3();
              const iq = new THREE.Quaternion();
              inner.getWorldPosition(ip);
              if (inner.getWorldQuaternion) inner.getWorldQuaternion(iq);
              innerAfter = { pos: ip.toArray(), quat: iq.toArray() };
            }
          } catch (error) {
            logger.warn("[Scene] Unexpected error during scene update", error);
          }

          devLog("[Scene] pose entry (post-apply)", {
            name,
            worldPos: worldPosAfter.toArray(),
            worldQuat: worldQuatAfter.toArray(),
            innerAfter,
          });
        }
      }
      return localMapped;
    };

    const relevantIncomingCount = names.reduce((count, rawName, idx) => {
      const pose = getPoseAt(idx);
      if (!pose) return count;
      const lowered = String(rawName || "").toLowerCase();
      if (lowered === "world") return count;
      return count + 1;
    }, 0);

    const strictMappedCount = applyMappedPoses({ strictPoseDriven: true });
    mappedCount = strictMappedCount;
    // Only fall back when strict filtering maps nothing. Applying every MuJoCo body in an
    // articulated scene double-drives fixed child bodies that should inherit parent motion.
    if (relevantIncomingCount > 0 && strictMappedCount === 0) {
      const relaxedMappedCount = applyMappedPoses({ strictPoseDriven: false });
      mappedCount = Math.max(strictMappedCount, relaxedMappedCount);
    }

    const sceneBodyCount = Object.keys(getSceneState().bodies || {}).length;
    if (sceneBodyCount > 0 && mappedCount === 0 && relevantIncomingCount > 0) {
      try {
        const signature = names.slice(0, 12).join("|");
        if (zeroBodySyncWarningSignatureRef.current !== signature) {
          zeroBodySyncWarningSignatureRef.current = signature;
          actions.addDiagnostic({
            source: "viewer",
            stage: "body-sync",
            level: "warn",
            message: "Simulation body names don't match scene - visuals may be out of sync.",
            data: {
              incomingBodyNames: names.slice(0, 12),
              incomingBodyCount: relevantIncomingCount,
            },
          });
        }
      } catch (error) {
        logger.warn("[Scene] Failed to record body sync warning", error);
      }
    }
    if (mappedCount === 0 && debugPoses) {
      try {
        const sceneKeys = Object.keys(sceneObjectsRef.current || {});
        logger.warn("[Scene] No bodies mapped. Scene keys:", sceneKeys);
        logger.warn("[Scene] Incoming physics body names:", names);
      } catch (error) {
        logger.warn("[Scene] Unexpected error during scene update", error);
      }
    }

    // Avoid index-based fallback mapping; only apply poses by matching names to prevent scrambling
    // If nothing mapped, keep the scene as-is and report mapping stats for debugging

    const testBoxVisualEnabled =
      typeof window !== "undefined" && window.__CRITTER_TEST_BOX_VISUAL === true;
    if (testBoxVisualEnabled) {
      const testBoxName = names.includes("testbox")
        ? "testbox"
        : names.includes("body_1")
          ? "body_1"
          : null;
      if (testBoxName) {
        const testBoxIndex = names.indexOf(testBoxName);
        const testBoxPose = getPoseAt(testBoxIndex);

        if (testBoxPose) {
          let testBoxObj = sceneRef.current?.getObjectByName("testCollisionBox");

          // Create test box visual if it doesn't exist
          if (!testBoxObj) {
            devLog("[Scene] Creating test box visual representation");
            const geometry = new THREE.BoxGeometry(0.2, 0.2, 0.2); // 0.1 * 2 = 0.2 for size
            const material = new THREE.MeshBasicMaterial({
              color: 0xff0000,
              wireframe: true,
              transparent: true,
              opacity: 0.8,
              side: THREE.DoubleSide,
            });
            testBoxObj = new THREE.Mesh(geometry, material);
            testBoxObj.name = "testCollisionBox";
            sceneRef.current?.add(testBoxObj);
            devLog("[Scene] Test box visual created and added to scene");
          }

          if (testBoxObj) {
            const [px, py, pz, qw, qx, qy, qz] = testBoxPose;
            testBoxObj.position.set(px, pz, -py);
            testBoxObj.quaternion.set(qx, qz, -qy, qw);
          }
        }
      }
    }

    try {
      // Update any active external-force arrows using the latest physics poses.
      const byBodyUuid = externalForceVizRef.current?.byBodyUuid;
      if (byBodyUuid && byBodyUuid.size) {
        const now = performance.now();
        for (const [bodyUuid, entry] of Array.from(byBodyUuid.entries())) {
          const arrow = entry?.arrow;
          const expiresAtMs = Number(entry?.expiresAtMs) || 0;
          if (!arrow) {
            byBodyUuid.delete(bodyUuid);
            continue;
          }
          if (expiresAtMs && now > expiresAtMs) {
            disposeAndRemove(arrow);
            byBodyUuid.delete(bodyUuid);
            continue;
          }

          const poseObj =
            sceneObjectsRef.current?.[bodyUuid] ||
            sceneObjectsRef.current?.[sanitizeMjcfName(bodyUuid)] ||
            null;

          const f = mujocoVecToViewer(entry?.force);
          const mag = f.length();
          const worldDir =
            mag > 1e-6 ? f.clone().multiplyScalar(1 / mag) : new THREE.Vector3(1, 0, 0);

          // Prefer UUID anchoring: parent the arrow under the body's poseGroup.
          if (poseObj && typeof poseObj.getWorldQuaternion === "function") {
            try {
              if (arrow.parent !== poseObj) {
                if (arrow.parent) arrow.parent.remove(arrow);
                poseObj.add(arrow);
              }
            } catch {
              // Best-effort marker reparenting; fall back to world anchoring below.
            }
            arrow.visible = true;
            // Slight upward offset in the body's local frame.
            arrow.position.set(0, 0.03, 0);

            // Convert world direction into the body's local space so ArrowHelper direction is correct.
            const q = new THREE.Quaternion();
            poseObj.getWorldQuaternion(q);
            q.invert();
            const localDir = worldDir.clone().applyQuaternion(q);
            if (localDir.lengthSq() < 1e-10) localDir.set(1, 0, 0);
            localDir.normalize();

            const len = Math.max(0.12, Math.min(0.5, 0.12 + 0.06 * mag));
            arrow.setDirection(localDir);
            arrow.setLength(len, Math.max(0.03, len * 0.25), Math.max(0.02, len * 0.18));
            continue;
          }

          // Fallback: world anchoring using physics poses list.
          try {
            if (!arrow.parent && sceneRef.current) sceneRef.current.add(arrow);
          } catch {
            // Best-effort marker attachment.
          }
          const idx =
            names.indexOf(bodyUuid) !== -1
              ? names.indexOf(bodyUuid)
              : names.indexOf(sanitizeMjcfName(bodyUuid));
          const pose = idx >= 0 ? getPoseAt(idx) : null;
          if (!pose) {
            arrow.visible = false;
            continue;
          }
          arrow.visible = true;
          const px = Number(pose[0]) || 0;
          const py = Number(pose[1]) || 0;
          const pz = Number(pose[2]) || 0;
          arrow.position.set(px, pz + 0.03, -py);
          const len = Math.max(0.12, Math.min(0.5, 0.12 + 0.06 * mag));
          arrow.setDirection(worldDir);
          arrow.setLength(len, Math.max(0.03, len * 0.25), Math.max(0.02, len * 0.18));
        }
      }

      // Low-noise rollout probe: anomaly-only with signature dedupe (no per-frame spam).
      // Enable with: window.__rolloutReplayProbe = true
      const probeEnabled = typeof window !== "undefined" && window.__rolloutReplayProbe === true;
      if (probeEnabled) {
        const nowMs = Date.now();
        const throttleMs = 8000;
        if (nowMs - Number(rolloutProbeRef.current.lastWarnMs || 0) >= throttleMs) {
          let anomaly = null;
          let shouldCheckRootDrift = true;
          if (mappedCount < relevantIncomingCount) {
            const unresolved = [];
            for (let i = 0; i < names.length; i++) {
              const name = names[i];
              const pose = getPoseAt(i);
              if (!pose) continue;
              let obj = sceneObjectsRef.current[name] || null;
              if (!obj && typeof name === "string") {
                const mappedSceneKey = bodyAliasToSceneKey?.get(name);
                if (mappedSceneKey) {
                  obj =
                    sceneObjectsRef.current[mappedSceneKey] ||
                    sceneObjectsRef.current[sanitizeMjcfName(mappedSceneKey)] ||
                    null;
                }
              }
              if (!obj) unresolved.push(name);
            }
            const unresolvedRelevant = unresolved.filter((name) => {
              const lowered = String(name || "").toLowerCase();
              // `world` is commonly present in physics payloads and intentionally has no scene object.
              return lowered !== "world";
            });
            if (unresolvedRelevant.length > 0) {
              shouldCheckRootDrift = false;
              anomaly = {
                kind: "partial-mapping",
                mapped: Number(mappedCount),
                incoming: Number(relevantIncomingCount),
                missingNames: unresolvedRelevant.slice(0, 3),
                missingCount: Number(unresolvedRelevant.length),
              };
            }
          }
          if (shouldCheckRootDrift) {
            // Probe floating-base root consistency against expected local pose.
            const rootName = names.find((n) => rootBodyUuids.has(n)) || null;
            if (rootName) {
              const rootIdx = names.indexOf(rootName);
              const rootPose = rootIdx >= 0 ? getPoseAt(rootIdx) : null;
              let rootObj =
                sceneObjectsRef.current[rootName] ||
                sceneObjectsRef.current[sanitizeMjcfName(rootName)] ||
                null;
              if (!rootObj && typeof rootName === "string") {
                const mappedSceneKey = bodyAliasToSceneKey?.get(rootName);
                if (mappedSceneKey) {
                  rootObj =
                    sceneObjectsRef.current[mappedSceneKey] ||
                    sceneObjectsRef.current[sanitizeMjcfName(mappedSceneKey)] ||
                    null;
                }
              }
              if (rootObj && Array.isArray(rootPose) && rootPose.length >= 7) {
                const expected = convertPoseToLocal(
                  [
                    rootPose[0],
                    rootPose[1],
                    rootPose[2],
                    rootPose[3],
                    rootPose[4],
                    rootPose[5],
                    rootPose[6],
                  ],
                  modelGroupRef.current,
                  rootObj.parent
                );
                if (expected) {
                  const posErr = expected.position.distanceTo(rootObj.position);
                  const qDot = Math.abs(expected.quaternion.dot(rootObj.quaternion));
                  const quatErrRad = 2 * Math.acos(Math.max(-1, Math.min(1, qDot)));
                  if (posErr > 0.05 || quatErrRad > 0.25) {
                    anomaly = {
                      kind: "root-drift",
                      rootName,
                      posErr: Number(posErr.toFixed(4)),
                      quatErrRad: Number(quatErrRad.toFixed(4)),
                    };
                  }
                }
              }
            }
          }
          if (anomaly) {
            const nextSignature = JSON.stringify(anomaly);
            if (nextSignature !== rolloutProbeRef.current.lastSignature) {
              rolloutProbeRef.current.lastWarnMs = nowMs;
              rolloutProbeRef.current.lastSignature = nextSignature;
              logger.warn("[RolloutProbe]", anomaly);
            }
          }
        }
      }

      // Scene-apply fidelity probe: compare expected world poses vs rendered world poses.
      // Enable with: window.__rolloutSceneApplyProbe = true
      const sceneApplyProbeEnabled =
        typeof window !== "undefined" && window.__rolloutSceneApplyProbe === true;
      if (sceneApplyProbeEnabled && mappedCount > 0 && names.length > 0) {
        const key = `${names.length}:${names.slice(0, 4).join("|")}`;
        if (rolloutProbeRef.current.sceneApplyProbeKey !== key) {
          rolloutProbeRef.current.sceneApplyProbeKey = key;
          rolloutProbeRef.current.sceneApplyProbeDone = false;
        }
        if (!rolloutProbeRef.current.sceneApplyProbeDone) {
          const calcErr = (expectedPos, expectedQuat, actualPos, actualQuat) => {
            const dx = expectedPos.x - actualPos.x;
            const dy = expectedPos.y - actualPos.y;
            const dz = expectedPos.z - actualPos.z;
            const posErr = Math.sqrt(dx * dx + dy * dy + dz * dz);
            const qDot = Math.abs(expectedQuat.dot(actualQuat));
            const quatErrRad = 2 * Math.acos(Math.max(-1, Math.min(1, qDot)));
            return { posErr, quatErrRad };
          };

          const resolveObj = (name) => {
            let obj = sceneObjectsRef.current[name] || null;
            if (!obj && typeof name === "string") {
              const mappedSceneKey = bodyAliasToSceneKey?.get(name);
              if (mappedSceneKey) {
                obj =
                  sceneObjectsRef.current[mappedSceneKey] ||
                  sceneObjectsRef.current[sanitizeMjcfName(mappedSceneKey)] ||
                  null;
              }
            }
            return obj;
          };

          let sampled = 0;
          let worstObj = { name: null, posErr: 0, quatErrRad: 0 };
          let worstInner = { name: null, posErr: 0, quatErrRad: 0 };

          for (let i = 0; i < names.length && sampled < 4; i += 1) {
            const name = names[i];
            const pose = getPoseAt(i);
            if (!pose || String(name || "").toLowerCase() === "world") continue;
            const obj = resolveObj(name);
            if (!obj) continue;
            const expectedWorld = convertPoseToLocal(
              [pose[0], pose[1], pose[2], pose[3], pose[4], pose[5], pose[6]],
              modelGroupRef.current,
              null
            );
            if (!expectedWorld) continue;

            const objWorldPos = new THREE.Vector3();
            const objWorldQuat = new THREE.Quaternion();
            obj.getWorldPosition(objWorldPos);
            if (obj.getWorldQuaternion) obj.getWorldQuaternion(objWorldQuat);
            const objErr = calcErr(
              expectedWorld.position,
              expectedWorld.quaternion,
              objWorldPos,
              objWorldQuat
            );
            if (
              objErr.posErr > worstObj.posErr ||
              (Math.abs(objErr.posErr - worstObj.posErr) < 1e-8 &&
                objErr.quatErrRad > worstObj.quatErrRad)
            ) {
              worstObj = {
                name: String(name || ""),
                posErr: Number(objErr.posErr.toExponential(3)),
                quatErrRad: Number(objErr.quatErrRad.toExponential(3)),
              };
            }

            try {
              const inner =
                obj && typeof obj.getObjectByName === "function" ? obj.getObjectByName(name) : null;
              if (inner && inner !== obj) {
                const innerWorldPos = new THREE.Vector3();
                const innerWorldQuat = new THREE.Quaternion();
                inner.getWorldPosition(innerWorldPos);
                if (inner.getWorldQuaternion) inner.getWorldQuaternion(innerWorldQuat);
                const innerErr = calcErr(
                  expectedWorld.position,
                  expectedWorld.quaternion,
                  innerWorldPos,
                  innerWorldQuat
                );
                if (
                  innerErr.posErr > worstInner.posErr ||
                  (Math.abs(innerErr.posErr - worstInner.posErr) < 1e-8 &&
                    innerErr.quatErrRad > worstInner.quatErrRad)
                ) {
                  worstInner = {
                    name: String(name || ""),
                    posErr: Number(innerErr.posErr.toExponential(3)),
                    quatErrRad: Number(innerErr.quatErrRad.toExponential(3)),
                  };
                }
              }
            } catch {
              // Optional debug sampling should not affect pose application.
            }

            sampled += 1;
          }

          const status =
            worstObj.posErr > 5e-2 ||
            worstObj.quatErrRad > 2.5e-1 ||
            worstInner.posErr > 5e-2 ||
            worstInner.quatErrRad > 2.5e-1
              ? "mismatch"
              : "ok";
          const payload = {
            status,
            sampled,
            mapped: Number(mappedCount),
            incoming: Number(relevantIncomingCount),
            worstObj,
            worstInner,
          };
          if (status === "ok") logger.info("[RolloutSceneApplyProbe]", payload);
          else logger.warn("[RolloutSceneApplyProbe]", payload);
          rolloutProbeRef.current.sceneApplyProbeDone = true;
        }
      }

      actions.setMappingStats(mappedCount, relevantIncomingCount);
    } catch (error) {
      logger.warn("[Scene] Unexpected error during scene update", error);
    }
  };

  return applyLatestPoses;
}
